import { ArrowRightStartOnRectangleIcon } from "@heroicons/react/20/solid";
import { useParams } from "react-router";

import { Paragraph } from "../../common/Atoms/Typography/Paragraph";
import { classNames } from "../../common/lib/classNames";
import { usePortalInvoice } from "../hooks/usePortalInvoice";
import { usePortalQuote } from "../hooks/usePortalQuote";
import { useSession } from "../hooks/useSession";
import { PORTAL_AUTH_CODE_STORAGE_KEY } from "../constants";
import { useFetchContactQuery } from "../services/api/contact";
import { usePortalLogoutMutation } from "../services/api/session";

export function Footer() {
  const { code } = useParams();
  const session = useSession();
  const [portalLogout, { isLoading: logoutLoading }] = usePortalLogoutMutation();

  const { data: contactResult, refetch: refetchContact } = useFetchContactQuery(
    { code: code ?? ``, sessionId: session?.id ?? `` },
    {
      skip: !window.location.pathname.includes(`/c/`) || !session?.id || !code,
    },
  );

  const { data: invoiceResult, refetch: refetchInvoice } = usePortalInvoice();
  const { data: quoteResult, refetch: refetchQuote } = usePortalQuote();

  const secureSessionUserEmail =
    contactResult?.secureSessionUserEmail
    ?? invoiceResult?.secureSessionUserEmail
    ?? quoteResult?.secureSessionUserEmail;

  const secureSessionUserIsAppUser =
    contactResult?.secureSessionUserIsAppUser
    ?? invoiceResult?.secureSessionUserIsAppUser
    ?? quoteResult?.secureSessionUserIsAppUser;

  const onLogout = () => {
    if (!code || !session?.id || !secureSessionUserEmail) {
      return;
    }

    sessionStorage.removeItem(PORTAL_AUTH_CODE_STORAGE_KEY);

    void portalLogout({
      code,
      sessionId: session.id,
      sessionEmail: secureSessionUserEmail,
    })
      .unwrap()
      .then(() => {

        if (contactResult) {
          refetchContact();
        }

        if (invoiceResult) {
          refetchInvoice();
        }

        if (quoteResult) {
          refetchQuote();
        }
      });
  };

  return (
    <footer>
      <div
        className={ `w-full flex flex-col justify-center items-center gap-2 mt-12` }>
        { secureSessionUserEmail && (
          <div className={ `flex items-center justify-center gap-1.5` }>
            <Paragraph
              variant={ `secondary` }
              size={ `sm` }
              className={ `mb-0!` }
            >
              { `Logged in as ` }
              {
                secureSessionUserEmail
              }
            </Paragraph>
            { !secureSessionUserIsAppUser && (
              <button
                type={ `button` }
                onClick={ onLogout }
                disabled={ logoutLoading || !code || !session?.id }
                className={ classNames(
                  `rounded-sm p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800`,
                  `focus:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-400`,
                  (logoutLoading || !code || !session?.id) && `opacity-50 cursor-not-allowed`,
                ) }
                aria-label={ `Log out` }
              >
                <ArrowRightStartOnRectangleIcon className={ `h-4 w-4` }
                  aria-hidden={ `true` } />
              </button>
            ) }
          </div>
        ) }
        <a
          href={ `https://www.paidnice.com?source=portal_powered` }
          target={ `_blank` }
          rel={ `noopener noreferrer` }
        >
          <Paragraph
            variant={ `link` }
          >
            {
              `Powered by Paidnice`
            }
          </Paragraph>
        </a>
      </div>
    </footer>
  );
}
